import api from "api/axios";

export interface HeaderInfo {
  name: string;
  location: string;
  email: string;
  phone: string;
  linkedin: string;
  github: string;
}

export interface SkillGroup {
  heading: string;
  skills: string[];
}

export interface WorkExperienceEntry {
  company: string;
  location: string;
  period: string;
  position: string;
  techStack: string;
  bullets: string[];
}

export interface EducationEntry {
  institution: string;
  degree: string;
  location: string;
  duration: string;
}

export interface ProjectEntry {
  title: string;
  url: string;
  techStack: string;
  description: string;
  bullets: string[];
}

export interface CvDocument {
  id: number;
  owner_type: string;
  owner_id: number;
  title: string;
  job_description: string;
  headline: string;
  summary: string;
  work_experience: WorkExperienceEntry[];
  skills: SkillGroup[];
  education: EducationEntry[];
  projects: ProjectEntry[];
  last_section_scores: Record<string, SectionScore> | null;
  holistic_review: HolisticReview | null;
  header_info: HeaderInfo | null;
  category_id: number | null;
  last_enhanced_at: string | null;
  is_active: boolean;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScoreCriterion {
  name: string;
  score: number;
  reason: string;
  suggestion: string;
}

export interface SectionScore {
  total_score: number;
  top_priority_fix: string;
  criteria: ScoreCriterion[];
}

export interface HolisticReview {
  overall_score: number;
  recruiter_summary: string;
  cross_section_issues: { issue: string; sections_involved: string[]; fix: string }[];
  per_section_notes: Record<string, { score: number; note: string; fix: string }>;
  top_priority_fix: string;
  top_priority_sections: string[];
}

export type SectionKey = "headline" | "summary" | "work_experience" | "skills" | "education" | "projects";

export interface CvDocumentCreate {
  title?: string;
  job_description?: string;
  headline?: string;
  summary?: string;
  work_experience?: WorkExperienceEntry[];
  skills?: SkillGroup[];
  education?: EducationEntry[];
  projects?: ProjectEntry[];
  header_info?: Partial<HeaderInfo> | null;
  category_id?: number | null;
}

export interface CvDocumentUpdate extends CvDocumentCreate {}

export interface EnhanceResult {
  document: CvDocument;
  improvements: SectionScore | null;
}

export interface ReviewResult {
  document: CvDocument;
  review: HolisticReview | null;
}

/** Returns the correct API base path based on the authenticated user type. */
export function cvApiBase(): string {
  return localStorage.getItem("authUserType") === "admin"
    ? "/admin/cv-processor"
    : "/employee/cv-processor";
}

export async function listDocuments(): Promise<CvDocument[]> {
  const res = await api.get(`${cvApiBase()}/documents`);
  return res.data;
}

export async function createDocument(payload: CvDocumentCreate): Promise<CvDocument> {
  const res = await api.post(`${cvApiBase()}/documents`, payload);
  return res.data;
}

export interface CvDocumentCreateAsEmployee extends CvDocumentCreate {
  employee_code: string;
}

/** Admin-only: saves a document directly into a Technical Manager's own CV
 * collection (e.g. an enhanced CV produced while admin ran Job Board on that
 * employee's behalf). */
export async function createEmployeeDocumentAsAdmin(payload: CvDocumentCreateAsEmployee): Promise<CvDocument> {
  const res = await api.post(`/admin/cv-processor/employee-documents`, payload);
  return res.data;
}

export async function getDocument(docId: number): Promise<CvDocument> {
  const res = await api.get(`${cvApiBase()}/documents/${docId}`);
  return res.data;
}

export async function getEmployeeDocumentAsAdmin(docId: number): Promise<CvDocument> {
  const res = await api.get(`/admin/cv-processor/employee-documents/${docId}`);
  return res.data;
}

export async function updateDocument(docId: number, payload: CvDocumentUpdate): Promise<CvDocument> {
  const res = await api.patch(`${cvApiBase()}/documents/${docId}`, payload);
  return res.data;
}

export async function listArchivedDocuments(): Promise<CvDocument[]> {
  const res = await api.get(`${cvApiBase()}/documents/archived`);
  return res.data;
}

export async function archiveDocument(docId: number): Promise<CvDocument> {
  const res = await api.post(`${cvApiBase()}/documents/${docId}/archive`);
  return res.data;
}

export async function restoreDocument(docId: number): Promise<CvDocument> {
  const res = await api.post(`${cvApiBase()}/documents/${docId}/restore`);
  return res.data;
}

export async function enhanceSection(
  docId: number,
  sectionKey: SectionKey,
  opts: { previous_review?: SectionScore | null; holistic_context?: object | null } = {}
): Promise<EnhanceResult> {
  const res = await api.post(`${cvApiBase()}/documents/${docId}/enhance/${sectionKey}`, opts);
  return res.data;
}

export async function reviewDocument(
  docId: number,
  changedSectionKeys: SectionKey[] = []
): Promise<ReviewResult> {
  const res = await api.post(`${cvApiBase()}/documents/${docId}/review`, {
    changed_section_keys: changedSectionKeys,
  });
  return res.data;
}

export async function enhanceAllPreview(docId: number, jobDescription: string): Promise<CvDocument> {
  const res = await api.post(`${cvApiBase()}/documents/${docId}/enhance-all-preview`, {
    job_description: jobDescription,
  });
  return res.data;
}

export async function scoreSectionAndCv(docId: number, sectionKey: SectionKey): Promise<CvDocument> {
  const res = await api.post(`${cvApiBase()}/documents/${docId}/score/${sectionKey}`, {});
  return res.data;
}

export async function scoreAllSections(docId: number): Promise<CvDocument> {
  const res = await api.post(`${cvApiBase()}/documents/${docId}/score-all`, {});
  return res.data;
}

export interface LastEnhancement {
  job_description: string;
  result: CvDocument;
  logs: any[];
  enhanced_at: string;
  provider?: "openai" | "deepseek";
}

export async function getLastEnhancement(docId: number): Promise<LastEnhancement> {
  const res = await api.get(`${cvApiBase()}/documents/${docId}/last-enhancement`);
  return res.data;
}
